import { action, computed, makeObservable, observable } from 'mobx';
import merge from 'lodash/merge';

import { getApiErrorMessage } from '@components/common/util/util';
import { SourceEvent } from '@components/sources/liveEvents/types';
import {
  EventStatParams,
  TrackEventParams,
} from '@components/sources/source-view/source-events/hooks';
import {
  EventsWithViolationParams,
  EventsWithViolationResponse,
  TrackingPlanStats,
  TrackingPlanStatsParams,
} from '@components/sources/source-view/source-events/trackingPlan/hooks';
import { BATCH_SIZE } from '@components/sources/source-view/source-events/utils';
import { apiAuthCaller } from '@services/apiCaller';
import { IConnectionsStore } from '@stores/connections';
import { IDestinationsListStore } from '@stores/destinationsList';
import { IMessageStore } from '@stores/messages';
import { ISourceDefinitionsListStore } from '@stores/sourceDefinitionsList';
import { ISourcesListStore } from '@stores/sourcesList';
import { ITrackingPlanListStore } from '@stores/trackingPlans/trackingPlansList';
import { IUserStore } from '@stores/user';
import { IWorkspaceStore } from '@stores/workspace';
import { Analytics } from '@lib/analytics';

import { IDestinationStore } from '../destination';
import { BotBlockingEventManagementConfig, WarehouseSourceOrigin } from '../types';
import { getOrigin } from '../util';
import {
  EventStatsResponse,
  Source,
  SourceConfig,
  SourceCategory,
  EventMetricsAPIResponseV2,
  BotMetricsResponse,
} from './types';
import { getSourceCategory } from './utils';

export type SecretConfig = Record<string, unknown>;

export type EventGraphMetrics = {
  bucket: number;
  bot?: BotMetrics;
  ingested: number;
  blocked?: number;
};

export interface EventStats {
  graph: EventGraphMetrics[];
  bot?: BotMetrics;
  ingested: number;
  blocked?: number;
}

interface BotMetrics {
  total: number;
  /**
   * Number of bot events that were successfully ingested after deduplication.
   * This is undefined in the legacy API format where we only track total bot events.
   * Will be defined once the new API format is fully deployed.
   */
  ingested: number | undefined;
}

export interface TrackEventsResponse {
  data?: TrackEvent[];
  hasMore: boolean;
}

interface TrackEvent {
  name: string;
  type: string;
  count: number;
  lastSent: number;
}

export interface TPGlobalConfig {
  dropUnplannedEvents?: boolean;
  dropUnplannedProperties?: boolean;
  dropOtherViolation?: boolean;
  propagateValidationErrors?: boolean;
}
interface TPGlobalConfigExtended extends TPGlobalConfig {
  sendViolatedEventsTo?: string;
}

export enum ITPSettingsType {
  identify = 'identify',
  group = 'group',
  page = 'page',
  screen = 'screen',
  track = 'track',
  global = 'global',
}

export type ITPSettingsEventType = Exclude<ITPSettingsType, ITPSettingsType.global>;

export interface ITPConfig extends Record<ITPSettingsType, TPGlobalConfig> {
  global: TPGlobalConfigExtended;
}

export interface ISourceTPConfig {
  config: ITPConfig;
  trackingPlanId: string;
  enabled: boolean;
}

export interface ISource extends Source {
  writeKey: string;
  createdAt: string;
  config: SourceConfig;
  secretConfig: SecretConfig;
  sourceDefinitionId: string;
  destinations: IDestinationStore[];
  transient: boolean;
  isGeoEnrichmentEnabled: boolean;
  trackingPlanConfig: ISourceTPConfig | undefined;
  origin: WarehouseSourceOrigin;
  updateConfig(config?: SourceConfig, hardUpdate?: boolean): Promise<boolean>;
}

type RootStore = {
  sourceDefinitionsListStore: ISourceDefinitionsListStore;
  sourcesListStore: ISourcesListStore;
  connectionsStore: IConnectionsStore;
  destinationsListStore: IDestinationsListStore;
  userStore: IUserStore;
  messagesStore: IMessageStore;
  workspaceStore: IWorkspaceStore;
  trackingPlanListStore: ITrackingPlanListStore;
};

interface TrackingPlan {
  id: string;
  name: string;
  createdAt: string;
  version?: string;
}

export interface TrackingPlanWithVersions extends TrackingPlan {
  versions?: string[];
  activeVersion?: string;
}

type TrackingPlanVersions = Record<string, number[]>;

export interface ISourceStore extends ISource {
  eventStats: EventStats | undefined;
  trackEvents: TrackEventsResponse | undefined;
  rootStore: RootStore;
  setName(name: string): void;
  updateName(name: string): Promise<boolean>;
  getLiveEvents(latestId: number, from: number): Promise<SourceEvent[]>;
  getEventStats: (params: EventStatParams, callback: () => void) => void;
  getTrackEvents: (params: TrackEventParams, callback?: () => void) => void;
  getHistoricalTrackingPlans: (
    params: {
      start?: string;
    },
    trackingPlanId?: string,
  ) => Promise<TrackingPlanWithVersions[] | undefined>;
  getTrackingPlanStats: (params: TrackingPlanStatsParams) => Promise<TrackingPlanStats | undefined>;
  getTrackingPlanVersions: (params: {
    start?: string;
  }) => Promise<TrackingPlanVersions | undefined>;
  getEventsWithViolations: (
    params: EventsWithViolationParams,
  ) => Promise<EventsWithViolationResponse | undefined>;
  isEventStream: () => boolean;
  setBotBlockingConfig: (config: Partial<BotBlockingEventManagementConfig>) => Promise<void>;
  deleteBotBlockingConfig: () => Promise<void>;
  toggleEnabled: (status: boolean) => Promise<void>;
}

export type SourceConstructor = {
  id: string;
  name: string;
  createdAt: string;
  writeKey: string;
  enabled: boolean;
  isGeoEnrichmentEnabled: boolean;
  config: SourceConfig;
  secretConfig: SecretConfig;
  sourceDefinitionId: string;
  transient: boolean;
  trackingPlanConfig: ISourceTPConfig | undefined;
};

export class SourceStore implements ISourceStore {
  @observable public id: string;

  @observable public name: string;

  @observable public createdAt: string;

  @observable public writeKey: string;

  @observable public enabled: boolean;

  @observable public isGeoEnrichmentEnabled: boolean;

  @observable public config: SourceConfig;

  @observable public transient: boolean;

  @observable public secretConfig: SecretConfig;

  @observable public sourceDefinitionId: string;

  @observable public trackingPlanConfig: ISourceTPConfig | undefined;

  @observable public rootStore: RootStore;

  @observable eventStats: EventStats | undefined;

  @observable trackEvents: TrackEventsResponse | undefined;

  constructor(source: SourceConstructor, rootStore: RootStore) {
    makeObservable(this);
    this.id = source.id;
    this.name = source.name;
    this.createdAt = source.createdAt;
    this.writeKey = source.writeKey;
    this.enabled = source.enabled;
    this.isGeoEnrichmentEnabled = source.isGeoEnrichmentEnabled;
    this.config = source.config;
    this.secretConfig = source.secretConfig;
    this.sourceDefinitionId = source.sourceDefinitionId;
    this.transient = source.transient;
    const prevState = rootStore.sourcesListStore.sourceById(this.id);
    this.eventStats = prevState?.eventStats;
    this.trackEvents = prevState?.trackEvents;
    this.trackingPlanConfig = source.trackingPlanConfig;
    this.rootStore = rootStore;
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @action.bound
  public async updateName(name: string): Promise<boolean> {
    const { messagesStore } = this.rootStore;

    try {
      await apiAuthCaller().patch(`/sources/${this.id}`, { name });
      this.name = name;
      return true;
    } catch (error) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to update source name'));
      return false;
    }
  }

  @action.bound
  public async toggleEnabled(status: boolean) {
    const { messagesStore } = this.rootStore;

    try {
      await apiAuthCaller().patch(`/sources/${this.id}`, { enabled: status });
      this.enabled = status;
      messagesStore.showSuccessMessage(`source ${status ? 'enabled' : 'disabled'} successfully`);
      if (!status) {
        Analytics.track({
          event: 'sourceDisabled',
          properties: { sourceId: this.id, sourceType: this.sourceDef.name },
        });
      }
    } catch (error) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to update source status'));
    }
  }

  @computed get sourceDef() {
    return this.rootStore.sourceDefinitionsListStore.getById(this.sourceDefinitionId)!;
  }

  @computed get destinations() {
    const destIds = this.rootStore.connectionsStore.connections[this.id] || [];
    return this.rootStore.destinationsListStore.destinations.filter((dest) =>
      destIds.includes(dest.id),
    );
  }

  @computed get category() {
    return this.sourceDef.category;
  }

  @action.bound
  isEventStream() {
    return !this.category || this.category === 'webhook';
  }

  @action.bound
  public async updateConfig(config: SourceConfig, hardUpdate = false) {
    const { messagesStore } = this.rootStore;
    try {
      const updateUrl = `/sources/${this.id}${hardUpdate ? '?hardUpdate=true' : ''}`;
      const res = await apiAuthCaller().post(updateUrl, {
        config,
      });
      this.config = res.data.config;
      return (await res.status) === 200;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to update source configuration.'),
      );
      return false;
    }
  }

  public async getLiveEvents(latestId: number, timestamp: number) {
    try {
      const resp = await apiAuthCaller().get(`/eventUploads`, {
        headers: { 'X-WRITE-KEY': this.writeKey },
        params: { id: latestId, from: timestamp },
      });
      return resp.data.map((e: { event: unknown }) => ({
        ...e,
        payload: e.event,
      }));
    } catch (err) {
      return [];
    }
  }

  async getTrackingPlanStats(
    params: TrackingPlanStatsParams,
  ): Promise<TrackingPlanStats | undefined> {
    try {
      const { data } = await apiAuthCaller().get<TrackingPlanStats>(
        `/sources/${this.id}/trackingPlans/eventFlowGraph`,
        {
          params,
        },
      );

      const { aggregates, totalEventsDropped, totalEventsValidated, totalEventsWithViolations } =
        data;

      return {
        aggregates: aggregates.map((s) => ({
          ...s,
          eventsDropped: s.eventsDropped || 0,
          eventsWithViolations: s.eventsWithViolations || 0,
        })),
        totalEventsDropped: totalEventsDropped || 0,
        totalEventsValidated: totalEventsValidated || 0,
        totalEventsWithViolations: totalEventsWithViolations || 0,
      };
    } catch (err) {
      this.rootStore.messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to get source events violations'),
      );
    }
    return undefined;
  }

  async getEventStats(params: EventStatParams, callback: () => void) {
    try {
      const { data } = await apiAuthCaller().get(`/sources/${this.id}/eventMetrics`, {
        params,
      });

      this.eventStats = { graph: [], ingested: 0 };

      if ((data as EventStatsResponse).aggregates) {
        const { aggregates, totalSent } = data as EventStatsResponse;
        this.eventStats = {
          graph: aggregates.map((s) => ({ bucket: s.bucket, ingested: s.sentCount || 0 })),
          ingested: totalSent || 0,
        };
      } else {
        const { data: aggregates, meta } = data as EventMetricsAPIResponseV2;
        /**
         * Bot metrics transformation
         *
         * The API response format for bot metrics is transitioning:
         * - Legacy format: bot is a number representing total events received that were identified as bot
         * - New format: bot is an object with { total: number, ingested: number }
         *
         * This transformation handles both formats until the new API is fully deployed.
         * Once the transition is complete, we can simplify this to only handle the object format.
         */
        const transformBotMetrics = (
          botData: number | BotMetricsResponse | undefined,
        ): BotMetrics | undefined => {
          if (botData === undefined) {
            return undefined;
          }

          return {
            total: typeof botData === 'number' ? botData : botData?.total ?? 0,
            ingested: typeof botData === 'number' ? undefined : botData?.ingested ?? 0,
          };
        };

        this.eventStats = {
          graph: aggregates.map((s) => ({
            bucket: s.bucket,
            ingested: s.ingested,
            bot: transformBotMetrics(s.bot),
            blocked: s.blocked,
          })),
          bot: transformBotMetrics(meta.bot),
          ingested: meta.ingested || 0,
          blocked: meta.blocked,
        };
      }
    } catch (err) {
      this.rootStore.messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to get source events data'),
      );
    } finally {
      callback();
    }
  }

  // Returns the trackings plans applied to the source after the given start date
  async getHistoricalTrackingPlans(
    {
      start,
    }: {
      start?: string;
    },
    // if trackingPlanId is provided, only the versions of the specified tracking plan will be returned
    trackingPlanId?: string,
  ): Promise<TrackingPlanWithVersions[] | undefined> {
    let trackingPlanList: TrackingPlan[] = [];
    if (trackingPlanId) {
      const defaultTrackingPlan = this.rootStore.trackingPlanListStore.getById(trackingPlanId);
      if (defaultTrackingPlan) {
        trackingPlanList = [
          {
            ...defaultTrackingPlan,
          },
        ];
      }
    } else {
      // get the previous applied tracking plans
      const { data = [] as TrackingPlan[] } = await apiAuthCaller().get<TrackingPlan[]>(
        `/sources/${this.id}/trackingPlans`,
        {
          params: {
            start,
          },
        },
      );
      trackingPlanList = data;
    }

    let currentTrackingPlan = trackingPlanList.find(
      (tp: TrackingPlan) => tp.id === this.trackingPlanConfig?.trackingPlanId,
    );

    // In some cases the tracking plan list might not contain the current tracking plan
    if (!currentTrackingPlan) {
      currentTrackingPlan = this.rootStore.trackingPlanListStore.getById(
        this.trackingPlanConfig?.trackingPlanId || '',
      );
    }

    // Filter out the current tracking plan from the list to avoid duplicates
    trackingPlanList = trackingPlanList.filter(
      (tp: TrackingPlan) => tp.id !== currentTrackingPlan?.id,
    );

    if (currentTrackingPlan) {
      trackingPlanList.unshift(currentTrackingPlan);
    }

    const versions = (await this.getTrackingPlanVersions({ start })) || {};

    return trackingPlanList.map((tp) => {
      const tpVersions: string[] = versions[tp.id as keyof typeof versions]?.map(String) || [];
      const activeVersion = String(
        tp.version || this.rootStore.trackingPlanListStore.getById(tp.id)?.version,
      );
      // Ensure the active version is included in the list of versions
      if (activeVersion && !tpVersions.includes(activeVersion)) {
        tpVersions.push(activeVersion);
      }
      return {
        ...tp,
        versions: tpVersions,
        activeVersion,
      };
    });
  }

  // Returns the trackings plans version of the source after the given start date
  async getTrackingPlanVersions({ start }: { start?: string }): Promise<TrackingPlanVersions> {
    const { data } = await apiAuthCaller().get<TrackingPlanVersions>(
      `/sources/${this.id}/trackingPlanVersions`,
      {
        params: {
          start,
        },
      },
    );
    return data;
  }

  getTrackEvents(params: TrackEventParams, callback?: () => void) {
    const isFirstCall = params.page === 0;
    if (isFirstCall) {
      this.trackEvents = undefined;
    }
    const { messagesStore } = this.rootStore;
    apiAuthCaller()
      .get(`/sources/${this.id}/events`, { params })
      .then((res) => {
        this.trackEvents = {
          ...this.trackEvents,
          hasMore: res.data.length === BATCH_SIZE,
        };
        if (res.data.length === 0 && !isFirstCall) {
          messagesStore.showErrorMessage('No more events found');
        } else {
          this.trackEvents.data = isFirstCall ? res.data : this.trackEvents.data?.concat(res.data);
        }
      })
      .catch((err) => {
        messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get event names list'));
      })
      .finally(() => {
        callback?.();
      });
  }

  async getEventsWithViolations(
    params: EventsWithViolationParams,
  ): Promise<EventsWithViolationResponse | undefined> {
    const { messagesStore } = this.rootStore;
    try {
      const response = await apiAuthCaller().get(`/sources/${this.id}/trackingPlans/eventMetrics`, {
        params,
      });
      return response.data as EventsWithViolationResponse;
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to get event names list'));
    }
    return undefined;
  }

  @computed get origin() {
    return getOrigin(this.config);
  }

  /**
   * Calculate the source type based on the source definition category and
   * source config origin
   */

  @computed get sourceCategory(): SourceCategory {
    return getSourceCategory(this.sourceDef.category, this.config);
  }

  @action.bound
  async setBotBlockingConfig(config: Partial<BotBlockingEventManagementConfig>) {
    const { messagesStore, workspaceStore } = this.rootStore;
    const oldConfig: BotBlockingEventManagementConfig =
      this.config.botBlocking?.eventManagement || ({} as BotBlockingEventManagementConfig);
    try {
      let input = config;
      if (input.enabled === true && !input.action) {
        input = {
          enabled: true,
          action: oldConfig?.action || workspaceStore.botBlocking.eventManagement?.action || 'flag',
        };
      }
      if (input.enabled === false) {
        input = {
          enabled: false,
        };
      }
      const newConfig: BotBlockingEventManagementConfig = merge(
        {},
        oldConfig,
        input,
      ) as BotBlockingEventManagementConfig;
      this.config.botBlocking = { eventManagement: newConfig };
      await apiAuthCaller().patch(`/sources/${this.id}/bot-event-management`, input);
    } catch (err) {
      this.config.botBlocking = { eventManagement: oldConfig };
      messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to update bot blocking config'),
      );
    }
  }

  @action.bound
  async deleteBotBlockingConfig() {
    const { messagesStore } = this.rootStore;
    try {
      await apiAuthCaller().delete(`/sources/${this.id}/bot-event-management`);
      this.config.botBlocking = undefined;
    } catch (err) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(err, 'Failed to delete bot blocking config'),
      );
    }
  }
}
